import * as z from 'zod';
import { isErrorCode } from '../errors/codes.js';
import { isMessageKey } from '../i18n/index.js';
import { isPermittedPlaceholder } from '../i18n/keys.js';
import { MAX_NOTES_LENGTH } from '../limits.js';
import { idempotencyKeySchema, machineKeySchema, shortRefSchema, strict, uuidSchema, } from './common.js';
import { fieldTypeSchema } from './field.js';
import { personStatusSchema } from './person.js';
/**
 * Importing people from the facility's own intake spreadsheet.
 *
 * **The server interprets and the browser only holds decisions.** An upload returns the sheet as
 * TYPED raw cells, exactly as the workbook stored them; every later step sends those cells back
 * together with the operator's decisions, and the server answers with the whole interpretation
 * again. Nothing is kept between requests and nothing is written until a commit, which is the same
 * `persons.create` a registration runs, one row at a time. There is therefore one interpreter and
 * one validator, and "ready" in the review can only mean what the commit will accept.
 *
 * **Fixes, suggestions and decisions are three different things.** A FIX is applied because it
 * cannot be wrong (quotes stripped, a document number taken out of a sentence, a date written
 * unambiguously) and the row says so. A SUGGESTION is an interpretation that could be wrong — a
 * nationality matched by spelling distance, a column's day/month order read off its number format
 * — and it is never applied until the operator accepts it, by sending it back as a decision. A
 * DECISION is anything the operator chose; it is keyed by a column and the value's normalized text
 * (or by a row), so one answer covers every row that carries the same text.
 *
 * **No per-person information is dropped.** Text a structured field could not hold travels into
 * the notes under its original column header; the interpretation never discards it.
 */
/** Non-empty rows one workbook may carry. A residence's whole history fits many times over. */
export const PERSON_IMPORT_MAX_ROWS = 5000;
/** Columns read per row; a cell beyond this position is never read. */
export const PERSON_IMPORT_MAX_COLUMNS = 100;
/** Characters one cell may hold, which is also the longest value any field accepts. */
export const PERSON_IMPORT_MAX_CELL_LENGTH = MAX_NOTES_LENGTH;
/** Sheets listed from one workbook. */
export const PERSON_IMPORT_MAX_SHEETS = 64;
/**
 * Rows committed by one request.
 *
 * Each row is its own transaction, so this bounds how long one request runs rather than how much
 * one transaction holds; it is also what keeps the audit row that lists the created people inside
 * the detail's size bound.
 */
export const PERSON_IMPORT_COMMIT_ROWS = 25;
/** Distinct values listed for one column, and for the whole analysis. */
export const PERSON_IMPORT_MAX_COLUMN_VALUES = 500;
export const PERSON_IMPORT_MAX_VALUES = 5000;
/** The highest reference number the automatic numbering will hand out. */
export const PERSON_IMPORT_MAX_REFERENCE = 99_999_999;
const columnIndexSchema = z
    .int()
    .min(0)
    .max(PERSON_IMPORT_MAX_COLUMNS - 1);
/** A sheet row number as the spreadsheet shows it, 1-based. */
const rowNumberSchema = z.int().min(1).max(1_048_576);
/**
 * How a date cell's number format orders the day and the month.
 *
 * Only an explicit custom format code states an order (`m/d/yyyy`, `dd/mm/yyyy`); the built-in
 * formats are displayed in the READER's locale, so for those the reader reports `null` rather
 * than a guess.
 */
export const importDateOrderSchema = z.enum(['dmy', 'mdy', 'ymd']);
/** One cell as the workbook stored it. An empty cell is `null` in its row. */
export const importCellSchema = z.discriminatedUnion('kind', [
    strict({ kind: z.literal('text'), text: z.string().max(PERSON_IMPORT_MAX_CELL_LENGTH) }),
    strict({ kind: z.literal('number'), value: z.number() }),
    /** A number under a date format: the serial, and the order its format code states, if any. */
    strict({ kind: z.literal('date'), serial: z.number(), order: importDateOrderSchema.nullable() }),
    strict({ kind: z.literal('boolean'), value: z.boolean() }),
    /** `#N/A` and its kind: a formula that failed. Carries nothing worth importing. */
    strict({ kind: z.literal('error') }),
]);
export const importRowSchema = strict({
    rowNumber: rowNumberSchema,
    /** Hidden in the sheet, which is shown rather than acted on. */
    hidden: z.boolean(),
    /** Column positions whose value was copied down from the first cell of a merged range. */
    merged: z.array(columnIndexSchema).max(PERSON_IMPORT_MAX_COLUMNS),
    /** By column position; trailing empty cells are not sent. */
    cells: z.array(importCellSchema.nullable()).max(PERSON_IMPORT_MAX_COLUMNS),
});
export const importColumnSchema = strict({
    index: columnIndexSchema,
    header: z.string().max(200),
});
/** Where a column goes. `age` is read to check the date of birth and is never stored. */
export const importTargetSchema = z.discriminatedUnion('kind', [
    strict({ kind: z.literal('field'), key: machineKeySchema }),
    strict({ kind: z.literal('notes') }),
    strict({ kind: z.literal('age') }),
    strict({ kind: z.literal('ignore') }),
]);
export const importMappingSchema = strict({
    column: columnIndexSchema,
    target: importTargetSchema,
});
/** A custom field the screen offers to create for a column no field answers to. */
export const importFieldSuggestionSchema = strict({
    key: machineKeySchema,
    label: z.string().min(1).max(120),
    type: fieldTypeSchema,
});
export const importColumnSummarySchema = strict({
    index: columnIndexSchema,
    header: z.string().max(200),
    /** The first few distinct values, for the operator to recognise the column by. */
    samples: z.array(z.string().max(120)).max(3),
    /**
     * Every row that holds anything holds this column's one value, so it says nothing about a person.
     * A value on some rows and blank on others is about those people, and is not constant.
     */
    constant: z.boolean(),
    suggestion: importFieldSuggestionSchema.nullable(),
});
export const importSheetSchema = strict({
    index: z
        .int()
        .min(0)
        .max(PERSON_IMPORT_MAX_SHEETS - 1),
    name: z.string().max(64),
});
/** The sheet, read: raw cells, the header it found, and the mapping it proposes. */
export const personImportWorkbookSchema = strict({
    sheets: z.array(importSheetSchema).min(1).max(PERSON_IMPORT_MAX_SHEETS),
    sheet: z
        .int()
        .min(0)
        .max(PERSON_IMPORT_MAX_SHEETS - 1),
    headerRow: rowNumberSchema,
    date1904: z.boolean(),
    columns: z.array(importColumnSummarySchema).max(PERSON_IMPORT_MAX_COLUMNS),
    rows: z.array(importRowSchema).max(PERSON_IMPORT_MAX_ROWS),
    mapping: z.array(importMappingSchema).max(PERSON_IMPORT_MAX_COLUMNS),
});
/** Which sheet to read, by position; the first sheet whose header is recognised when absent. */
export const personImportWorkbookQuerySchema = strict({
    sheet: z
        .string()
        .regex(/^\d{1,2}$/u, { error: 'errors.validation.invalid_format' })
        .transform(Number)
        .optional(),
});
/** A field value as a person record takes it; `null` in a decision means "leave it empty". */
export const importFieldValueSchema = z.union([
    z.string().max(PERSON_IMPORT_MAX_CELL_LENGTH),
    z.number(),
    z.boolean(),
    z.array(z.string().max(200)).max(50),
]);
/** One answer for every cell of a column that normalizes to `key` (`''` answers the empty ones). */
export const importValueDecisionSchema = strict({
    column: columnIndexSchema,
    key: z.string().max(PERSON_IMPORT_MAX_CELL_LENGTH),
    value: importFieldValueSchema.nullable(),
});
/** How the ambiguous dates of one column are to be read. */
export const importDateDecisionSchema = strict({
    column: columnIndexSchema,
    order: z.enum(['dmy', 'mdy']),
});
/**
 * What the operator decided about one row.
 *
 * `fields` holds only what was TOUCHED, and `null` in it means "leave this field empty"; a field
 * absent from it keeps its interpretation. `distinct` answers every "is this the same person?"
 * question on the row with "no, somebody else who lives here".
 */
export const importRowEditSchema = strict({
    rowNumber: rowNumberSchema,
    include: z.boolean().optional(),
    distinct: z.boolean().optional(),
    fields: z.record(machineKeySchema, importFieldValueSchema.nullable()).optional(),
});
export const personImportAnalysisRequestSchema = strict({
    date1904: z.boolean(),
    columns: z.array(importColumnSchema).max(PERSON_IMPORT_MAX_COLUMNS),
    rows: z.array(importRowSchema).max(PERSON_IMPORT_MAX_ROWS),
    mapping: z.array(importMappingSchema).max(PERSON_IMPORT_MAX_COLUMNS),
    values: z.array(importValueDecisionSchema).max(PERSON_IMPORT_MAX_VALUES),
    dates: z.array(importDateDecisionSchema).max(PERSON_IMPORT_MAX_COLUMNS),
    edits: z.array(importRowEditSchema).max(PERSON_IMPORT_MAX_ROWS),
    referenceStart: z.int().min(1).max(PERSON_IMPORT_MAX_REFERENCE).optional(),
});
/**
 * A change the interpretation made on its own, and shows.
 *
 * Every one of them is either lossless or leaves the original text in the notes.
 */
export const IMPORT_FIX_CODES = [
    /** A document number taken out of a cell that also held words. */
    'document_extracted',
    /** Separators the document field cannot hold (`/`, `.`) rewritten. */
    'document_normalized',
    /** Cell text no field could hold, kept in the notes under its column header. */
    'moved_to_notes',
    /** A day/month order the column's own unambiguous dates established, or the operator chose. */
    'date_order',
    /** A two-digit year given its century. */
    'two_digit_year',
    /** Only a year was known; the date is 1 January of it. */
    'year_only',
    /** A bare year typed into a date cell, read as that year rather than as a day count. */
    'typed_year',
    /** A date in a yes/no column: yes, and the date goes to the step's own date field. */
    'boolean_from_date',
    /** A step's date was given and its yes/no was not: yes. */
    'boolean_implied',
    /** A family unit given as an identifier: yes, and the identifier as its reference. */
    'family_reference',
    /** An empty reference, numbered. */
    'reference_assigned',
    /** A reference another row or another record already holds, renumbered. */
    'reference_renumbered',
    /** A value copied down from the first cell of a merged range. */
    'merged_fill',
    /** An included discharged row: its discharge date and reason are kept in the notes only. */
    'discharge_to_notes',
    /** A value the operator decided once for every row carrying it. */
    'value_decided',
];
export const importFixCodeSchema = z.enum(IMPORT_FIX_CODES);
/** Something about a row the operator has to see (`warning`) or settle (`error`). */
export const IMPORT_ISSUE_CODES = [
    /** A required field has no value. */
    'required_missing',
    /** The field's own validator refused the value; `messageKey` says why. */
    'invalid_value',
    /** A value this column's field cannot take was not recognised; settled once in Values. */
    'unresolved_value',
    /** A suggestion for this value or column waits to be accepted. */
    'suggestion_pending',
    /** Day and month could be either way round and nothing in the column settles it. */
    'ambiguous_date',
    /** Text that is not a date, a number or a document at all. */
    'unreadable_value',
    /** More than one document number in one cell. */
    'several_documents',
    /** The spreadsheet kept only fifteen digits of a longer number; the rest are gone. */
    'precision_lost',
    /** A document stored as a number, whose leading zeros the spreadsheet may have dropped. */
    'leading_zeros',
    /** The notes would exceed their length. */
    'notes_too_long',
    /** Another row carries the same document number. */
    'duplicate_document',
    /** Another row carries the same name and date of birth. */
    'duplicate_person',
    /** Another row carries the same name. */
    'duplicate_name',
    /** Another row carries the same bed. */
    'bed_shared',
    /** A record in the register may be this person. */
    'possible_registered',
    /** A reference typed by hand that another row or record already holds. */
    'reference_taken',
    /** The age in the sheet does not match the date of birth. */
    'age_mismatch',
    /** A NIE whose control letter does not match its number. */
    'nie_checksum',
    /** Hidden in the spreadsheet. */
    'hidden_row',
];
export const importIssueCodeSchema = z.enum(IMPORT_ISSUE_CODES);
export const importSeveritySchema = z.enum(['error', 'warning']);
/** An error message key, which is what a field validator's own refusal is keyed by. */
const messageKeySchema = z
    .string()
    .max(120)
    .refine(isMessageKey, { error: 'errors.validation.invalid_format' });
const messageParamsSchema = z
    .record(z.string(), z.union([z.string().max(64), z.number()]))
    .refine((params) => Object.keys(params).every(isPermittedPlaceholder), {
    error: 'errors.validation.invalid_format',
});
export const importIssueSchema = strict({
    /** The field the issue is about, or `null` for the row as a whole. */
    key: machineKeySchema.nullable(),
    code: importIssueCodeSchema,
    severity: importSeveritySchema,
    messageKey: messageKeySchema.optional(),
    params: messageParamsSchema.optional(),
    /** The other rows involved, for a duplicate. */
    rows: z.array(rowNumberSchema).max(20).optional(),
});
export const importMatchSignalSchema = z.enum(['passport', 'fingerprint', 'reference', 'name']);
/** A record in the register this row may be. Nothing about them beyond what identifies it. */
export const importMatchSchema = strict({
    personId: uuidSchema,
    shortRef: shortRefSchema,
    status: personStatusSchema,
    on: z.array(importMatchSignalSchema).min(1).max(4),
});
export const IMPORT_SKIP_REASONS = ['discharged', 'registered', 'excluded'];
export const importSkipReasonSchema = z.enum(IMPORT_SKIP_REASONS);
export const importRowStateSchema = z.enum(['ready', 'attention', 'skipped']);
export const analyzedRowSchema = strict({
    rowNumber: rowNumberSchema,
    state: importRowStateSchema,
    skip: importSkipReasonSchema.nullable(),
    /** The values a commit would send, blanks omitted. */
    values: z.record(machineKeySchema, importFieldValueSchema),
    fixes: z.array(strict({ key: machineKeySchema.nullable(), code: importFixCodeSchema })).max(64),
    issues: z.array(importIssueSchema).max(64),
    matches: z.array(importMatchSchema).max(10),
});
export const importValueStatusSchema = z.enum(['exact', 'suggested', 'decided', 'unresolved']);
/** One distinct value of a column whose field takes a closed set of answers. */
export const importDistinctValueSchema = strict({
    column: columnIndexSchema,
    /** The normalized text a decision is keyed by; `''` stands for every empty cell. */
    key: z.string().max(PERSON_IMPORT_MAX_CELL_LENGTH),
    /** One spelling of it as the sheet has it. */
    text: z.string().max(PERSON_IMPORT_MAX_CELL_LENGTH),
    count: z.int().min(1),
    /** What it becomes: applied when `exact` or `decided`, proposed when `suggested`. */
    value: importFieldValueSchema.nullable(),
    status: importValueStatusSchema,
    /** For a name that fits several countries, each of them. */
    candidates: z.array(z.string().max(64)).max(8),
});
export const importDateStatusSchema = z.enum([
    'evidence',
    'suggested',
    'decided',
    'unresolved',
    'conflicting',
]);
/** How one date column's ambiguous values are being read. */
export const importDateColumnSchema = strict({
    column: columnIndexSchema,
    status: importDateStatusSchema,
    /** The order applied to its ambiguous values, when one is. */
    order: z.enum(['dmy', 'mdy']).nullable(),
    /** The order its number format states, offered for acceptance. */
    suggestion: z.enum(['dmy', 'mdy']).nullable(),
    /** How many of its values are ambiguous. */
    ambiguous: z.int().nonnegative(),
});
const countSchema = z.int().nonnegative();
export const personImportAnalysisSchema = strict({
    rows: z.array(analyzedRowSchema).max(PERSON_IMPORT_MAX_ROWS),
    values: z.array(importDistinctValueSchema).max(PERSON_IMPORT_MAX_VALUES),
    dates: z.array(importDateColumnSchema).max(PERSON_IMPORT_MAX_COLUMNS),
    /** Where the automatic reference numbering started. */
    referenceStart: z.int().min(1).max(PERSON_IMPORT_MAX_REFERENCE),
    summary: strict({
        /** Rows that carry a person, which is every returned row. */
        rows: countSchema,
        ready: countSchema,
        attention: countSchema,
        skipped: countSchema,
        /** Rows with nothing that identifies anybody, dropped and only counted. */
        blank: countSchema,
        discharged: countSchema,
        registered: countSchema,
        excluded: countSchema,
        /** Values and columns whose suggestion is still waiting to be accepted. */
        suggestions: countSchema,
    }),
});
export const importCommitRowSchema = strict({
    rowNumber: rowNumberSchema,
    fields: z.record(machineKeySchema, z.unknown()),
});
export const personImportCommitRequestSchema = strict({
    rows: z.array(importCommitRowSchema).min(1).max(PERSON_IMPORT_COMMIT_ROWS),
    /** Present when the installation requires a privacy notice to be acknowledged per person. */
    consentAcknowledged: z.boolean().optional(),
    idempotencyKey: idempotencyKeySchema,
});
const errorCodeSchema = z
    .string()
    .max(64)
    .refine(isErrorCode, { error: 'errors.validation.invalid_format' });
const errorDetailSchema = strict({
    path: z.string().max(200),
    messageKey: messageKeySchema,
    params: messageParamsSchema.optional(),
});
/**
 * What happened to one row of a commit.
 *
 * `refused` is a refusal the create itself decided on (a value, a uniqueness, the consent), with
 * its own details. `failed` is a fault the row met, after which the request stops, and
 * `not_attempted` is every row it therefore never reached — so a caller knows exactly which rows
 * exist and which to send again.
 */
export const importCommitOutcomeSchema = z.discriminatedUnion('outcome', [
    strict({
        rowNumber: rowNumberSchema,
        outcome: z.literal('created'),
        personId: uuidSchema,
        shortRef: shortRefSchema,
    }),
    strict({
        rowNumber: rowNumberSchema,
        outcome: z.literal('refused'),
        code: errorCodeSchema,
        details: z.array(errorDetailSchema).max(64),
    }),
    strict({ rowNumber: rowNumberSchema, outcome: z.literal('failed'), code: errorCodeSchema }),
    strict({ rowNumber: rowNumberSchema, outcome: z.literal('not_attempted') }),
]);
export const personImportCommitResponseSchema = strict({
    rows: z.array(importCommitOutcomeSchema).max(PERSON_IMPORT_COMMIT_ROWS),
});
//# sourceMappingURL=person-import.js.map